import { useEffect } from 'react';

import type { QuickBooksControlCenterList } from '.';

// At most this many pages of one list (60 x 200 = 12,000 rows); a longer
// list is shown truncated and says so (money-lanes.tsx TruncatedNote /
// PartialTotalsNote). Production on 16 Oct 2026: 1,365 expense groups and
// 4,564 payout rows; the old bound of 25 pages (5,000 rows) was about to cut
// Payouts short.
export const MAX_CONTROL_CENTER_LIST_PAGES = 60;

// Pages read at the same time: one (OOM 17 Oct 2026). Four at a time made the
// QuickBooks worker build four whole lists at once (every page was a whole
// build there) and it ran out of memory on Production. The worker now cuts
// every page of one read from one build, so one page after another is fast;
// the first page still paints at once.
export const CONTROL_CENTER_LIST_PAGE_CONCURRENCY = 1;

// The worker's largest page (qb-worker quickbooksControlCenter.js MAX_LIMIT).
export const CONTROL_CENTER_SERVER_MAX_LIMIT = 200;

type PageAnswer = { data?: unknown; error?: unknown };

// True when a list read every page the server said it has. A list whose
// read stopped early (the page bound, a failed page, or a page from another
// revision) keeps the server's total, so `rows.length < total`; its sums are
// partial and the screen must say so.
export const controlCenterListComplete = (
  list?: QuickBooksControlCenterList | null
) => {
  const total = list?.pagination?.total;
  return typeof total !== 'number' || (list?.rows?.length ?? 0) >= total;
};

// The rest of a list after its first page, read page by page at the same
// page size (a few pages at a time). A page that fails, comes back empty or
// short, or was answered from another revision of the projection (another
// asOf or total) ends the read: the rows returned are always one revision,
// in order with no gap, and `pagination.total` keeps the server's total so
// the screen says how many it could not show.
// `keepReading` is asked before every page; false ends the read where it is
// (the rows read so far, an honest partial list).
export const readRemainingControlCenterPages = async (
  first: QuickBooksControlCenterList,
  read: (offset: number) => PageAnswer | PromiseLike<PageAnswer>,
  maxPages = MAX_CONTROL_CENTER_LIST_PAGES,
  keepReading: () => boolean | PromiseLike<boolean> = () => true
): Promise<QuickBooksControlCenterList> => {
  const total = first.pagination?.total;
  if (typeof total !== 'number' || (first.pagination?.offset ?? 0) !== 0) {
    return first;
  }
  const rows = [...(first.rows || [])];
  const pageSize = rows.length;
  if (pageSize === 0 || rows.length >= total) {
    return {
      ...first,
      rows,
      pagination: { total, offset: 0, limit: rows.length },
    };
  }
  const offsets: number[] = [];
  for (
    let offset = pageSize;
    offset < total && offsets.length + 1 < maxPages;
    offset += pageSize
  ) {
    offsets.push(offset);
  }
  const usable = (offset: number, page: PageAnswer) => {
    const data = page.data as QuickBooksControlCenterList | undefined;
    const count = data?.rows?.length ?? 0;
    return Boolean(
      !page.error &&
      data &&
      count > 0 &&
      data.asOf === first.asOf &&
      data.pagination?.total === total &&
      // Every page but the last is full, so the next offset has no gap.
      (count === pageSize || offset + count >= total)
    );
  };
  let stopped = false;
  for (
    let start = 0;
    start < offsets.length && !stopped;
    start += CONTROL_CENTER_LIST_PAGE_CONCURRENCY
  ) {
    if (!(await keepReading())) {
      break;
    }
    const batch = offsets.slice(
      start,
      start + CONTROL_CENTER_LIST_PAGE_CONCURRENCY
    );
    const answers = await Promise.all(
      batch.map(async (offset) => {
        try {
          return await read(offset);
        } catch (error) {
          return { error };
        }
      })
    );
    for (let index = 0; index < answers.length; index += 1) {
      const answer = answers[index];
      if (!usable(batch[index], answer)) {
        stopped = true;
        break;
      }
      rows.push(...(answer.data as QuickBooksControlCenterList).rows);
    }
  }
  return {
    ...first,
    rows,
    pagination: { total, offset: 0, limit: rows.length },
  };
};

// Page one first (coordinator rule, 16 Oct 2026: reading every page must
// never delay the first paint). A list asked with `allPages` answers with
// its first page at once, marked `restReading` while the other pages are
// read in the background; the cache entry is then replaced by the whole
// list (same revision) or, if that read stopped early, by what was read --
// `rows.length < total`, which every screen already says is partial.
export type ControlCenterListWithReading = QuickBooksControlCenterList & {
  restReading?: boolean;
  // A re-read of a list the screen already holds whole (17 Oct 2026): the
  // cache keeps showing the last whole list (its rows and totals) while the
  // new page one, kept here, and the rest of the new revision are read; the
  // whole new list then replaces it at once. Totals never flip mid-read.
  readingFirstPage?: QuickBooksControlCenterList;
};

// The page one a background read starts from: the new page one held beside
// the last whole list, or the cached page one itself.
export const readingFirstPageOf = (
  list: QuickBooksControlCenterList
): QuickBooksControlCenterList =>
  (list as ControlCenterListWithReading).readingFirstPage ?? list;

const withoutReadingMarks = (
  list: QuickBooksControlCenterList
): ControlCenterListWithReading => {
  const rest: ControlCenterListWithReading = { ...list };
  delete rest.readingFirstPage;
  return { ...rest, restReading: false };
};

export const controlCenterListReading = (
  list?: QuickBooksControlCenterList | null
) => Boolean((list as ControlCenterListWithReading | null)?.restReading);

// The first page as the cache keeps it until the rest is read. When the
// cache already holds this list whole (`previous`, a refresh), that whole
// list stays on screen and the new page one rides beside it until the new
// list is read whole (Production 17 Oct 2026: each re-read swapped 4,143
// deposit rows for the first 200, and the day count flipped 1,297 -> 107).
export const firstPageWhileReading = (
  first: QuickBooksControlCenterList,
  previous?: QuickBooksControlCenterList | null
): ControlCenterListWithReading => {
  if (
    (first.pagination?.offset ?? 0) !== 0 ||
    (first.rows?.length ?? 0) === 0 ||
    controlCenterListComplete(first)
  ) {
    return first;
  }
  if (
    previous &&
    (previous.rows?.length ?? 0) > 0 &&
    !controlCenterListReading(previous) &&
    controlCenterListComplete(previous)
  ) {
    return { ...previous, restReading: true, readingFirstPage: first };
  }
  return { ...first, restReading: true };
};

// The background read's answer replaces the cached first page only when
// both are the same revision and the cache still waits for it (a refetch
// in between keeps its own, newer answer).
export const listAfterBackgroundRead = (
  cached: ControlCenterListWithReading | undefined,
  read: QuickBooksControlCenterList
): ControlCenterListWithReading | undefined =>
  cached?.restReading && readingFirstPageOf(cached).asOf === read.asOf
    ? withoutReadingMarks(read)
    : undefined;

// Which background reads a screen still wants (OOM 17 Oct 2026). A screen
// that shows an `allPages` list says so while it shows it
// (useBackgroundListRead); when it stops (another tab, other arguments, the
// Control Center closed) the read ends before its next page and leaves the
// cache as it was (page one, still reading). A list no screen ever claimed
// (a direct caller) reads on, as before.
const listReadKey = (arg: object) =>
  JSON.stringify(
    Object.entries(arg)
      .filter(([name, value]) => name !== 'allPages' && value !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
  );
const wantedListReads = new Map<string, number>();
const claimedListReads = new Set<string>();
// The newest background read of each list: a newer one (a refetch of page
// one) ends the older one, so one list is never read twice at once.
const runningListReads = new Map<string, symbol>();

const wantListReadKey = (key: string) => {
  claimedListReads.add(key);
  wantedListReads.set(key, (wantedListReads.get(key) ?? 0) + 1);
  return () => {
    const left = (wantedListReads.get(key) ?? 1) - 1;
    if (left > 0) {
      wantedListReads.set(key, left);
    } else {
      wantedListReads.delete(key);
    }
  };
};

export const wantBackgroundListRead = (arg: object) =>
  wantListReadKey(listReadKey(arg));

// A screen showing an `allPages` list (`skip`: not shown right now). When it
// shows the list again and that list's read had ended early (the screen had
// left it), page one is read again and the rest after it.
export const useBackgroundListRead = (
  arg: object,
  skip: boolean,
  list: QuickBooksControlCenterList | null | undefined,
  refetch: () => unknown
) => {
  const key = listReadKey(arg);
  useEffect(() => {
    if (skip) {
      return undefined;
    }
    const release = wantListReadKey(key);
    if (controlCenterListReading(list) && !runningListReads.has(key)) {
      refetch();
    }
    return release;
    // Claimed when the list is shown or its arguments change; the list and
    // refetch of that moment decide whether an ended read starts again.
  }, [key, skip]);
};

const listReadWanted = (key: string) =>
  !claimedListReads.has(key) || (wantedListReads.get(key) ?? 0) > 0;

// Resolves once the page is visible (at once when it is, or outside a
// browser) with how long it waited: no page is read while the browser tab is
// hidden.
const untilPageVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden'
    ? Promise.resolve(0)
    : new Promise<number>((resolve) => {
        const hiddenAt = Date.now();
        const onChange = () => {
          if (document.visibilityState !== 'hidden') {
            document.removeEventListener('visibilitychange', onChange);
            resolve(Date.now() - hiddenAt);
          }
        };
        document.addEventListener('visibilitychange', onChange);
      });

// A read paused longer than this (the tab hidden) starts again from a fresh
// page one when the tab is shown: the worker keeps the build later pages are
// cut from for 120 s (qb-worker WHOLE_LIST_KEEP_MS), so the rest would be
// cut from another build (another asOf) and the list left partial.
export const CONTROL_CENTER_LIST_PAUSE_RESTART_MS = 60_000;

// The rest of an `allPages` list (RTK Query onQueryStarted of
// controlCenterList): once page one is in the cache, the other pages are
// read one at a time with the same arguments -- never while the browser tab
// is hidden, and only while a screen still shows the list -- and the cached
// page one is replaced by the whole list (listAfterBackgroundRead).
export const readRestInBackground = async <
  Arg extends { surface: string; allPages?: boolean },
>(
  arg: Arg,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: never) => unknown;
    queryFulfilled: Promise<{ data: QuickBooksControlCenterList }>;
  },
  // Reads one page of this list at an offset (the endpoint's own request).
  read: (offset: number) => PageAnswer | PromiseLike<PageAnswer>,
  setter: (
    arg: Arg,
    recipe: (
      cached: QuickBooksControlCenterList
    ) => QuickBooksControlCenterList | undefined
  ) => unknown
) => {
  if (!arg.allPages) {
    return;
  }
  let cachedFirst: QuickBooksControlCenterList;
  try {
    ({ data: cachedFirst } = await queryFulfilled);
  } catch {
    return;
  }
  if (!controlCenterListReading(cachedFirst)) {
    return;
  }
  const first = readingFirstPageOf(cachedFirst);
  const key = listReadKey(arg);
  const token = Symbol(key);
  runningListReads.set(key, token);
  let ended = false;
  let restart = false;
  const keepReading = async () => {
    const pausedMs = await untilPageVisible();
    ended = runningListReads.get(key) !== token || !listReadWanted(key);
    restart = !ended && pausedMs > CONTROL_CENTER_LIST_PAUSE_RESTART_MS;
    return !ended && !restart;
  };
  let whole: QuickBooksControlCenterList;
  let fresh = false;
  try {
    whole = await readRemainingControlCenterPages(
      first,
      read,
      MAX_CONTROL_CENTER_LIST_PAGES,
      keepReading
    );
    // Long paused: page one again, then the rest, all from one new build.
    for (let attempt = 0; restart && attempt < 2; attempt += 1) {
      restart = false;
      let again: PageAnswer;
      try {
        again = await read(0);
      } catch {
        again = {};
      }
      const page = again.data as QuickBooksControlCenterList | undefined;
      if (again.error || !page?.rows?.length) {
        ended = true;
        break;
      }
      whole = await readRemainingControlCenterPages(
        page,
        read,
        MAX_CONTROL_CENTER_LIST_PAGES,
        keepReading
      );
      fresh = true;
    }
  } finally {
    if (runningListReads.get(key) === token) {
      runningListReads.delete(key);
    }
  }
  if (ended || restart) {
    // Not shown any more, or a newer read took over: the cache keeps page
    // one (still reading) for that newer read or the screen's return.
    return;
  }
  dispatch(
    setter(arg, (cached) =>
      fresh
        ? // A read started again from a newer page one replaces the
          // cached page one it was started for.
          controlCenterListReading(cached)
          ? withoutReadingMarks(whole)
          : undefined
        : listAfterBackgroundRead(cached, whole)
    ) as never
  );
};
